import { useMemo } from "react";
import { ColDef, NewValueParams } from "ag-grid-community";

import IReportSelectedColumn from "../../../../../../shared/contracts/IReportSelectedColumn";
import IColumnAndFunction from "../../../../../../shared/contracts/IColumnAndFunction";
import IReportSaveSelectedColumnsBody from "../../../../../../shared/contracts/IReportSaveSelectedColumnsBody";
import useHelpers from "../../../../../../shared/services/helpers/useHelpers.hook";
import useAppStoreContext from "../../../../../../shared/context/app-store/app-store.context";
import { GROUP_FUNCTION } from "../../../../../../shared/contracts/TGroupFunctions";
import RowActionsCellRenderer from "./cell-renderers/row-actions.cell-renderer";
import { TGridApi } from "../../../../../../shared/contracts/TGridApi";
import IReportAvailableColumn from "../../../../../../shared/contracts/IReportAvailableColumn";

export default function useReportDataFunctionsDialogService() {

  const { extractKeys, getKeyByValue, extractEnumKeys, createMappings } = useHelpers();
  const { defaultAvailableColumns, defaultSelectedColumns } = useAppStoreContext();
  const NONE_KEY = getKeyByValue(GROUP_FUNCTION, GROUP_FUNCTION.NONE)!;
  const GROUP_KEY = getKeyByValue(GROUP_FUNCTION, GROUP_FUNCTION.GROUP)!;

  const getSaveSelectedColumnsBody = (
    selectedColumns: IReportSelectedColumn[]
  ): IReportSaveSelectedColumnsBody => {
    const body: IReportSaveSelectedColumnsBody = {
      reportColumnSelections: [...selectedColumns],
    };

    // ---
    return body;
  };


  const getColDefs = useMemo((): ColDef<IColumnAndFunction>[] => {

    const missingCols: IReportAvailableColumn[] = [];
    defaultSelectedColumns.forEach(selColumn => {
      if (defaultAvailableColumns.findIndex(x => x.columnName === selColumn.columnName) === -1) {
        missingCols.push({ ...selColumn });
      }
    });

    let _fieldDataSource = createMappings(
      [...defaultAvailableColumns, ...missingCols], "columnName", "columnDescription",
      { key: '-1', value: '...' }
    );

    return [

      {
        editable: false, headerName: "Actions",
        resizable: false, minWidth: 224,
        cellRenderer: RowActionsCellRenderer, rowDrag: true,
      },

      {
        editable: true, field: "field", headerName: "Column Name", flex: 2,
        cellEditor: "agSelectCellEditor", //'agRichSelectCellEditor',
        refData: _fieldDataSource, // dropdown-dataSource
        cellEditorParams: {
          values: extractKeys(_fieldDataSource),//extract the keys
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
          valueListMaxHeight: 220,
          // valueListMaxHeight: 220,
        },
      },

      {
        editable: true, field: "function", headerName: "Group Function", flex: 1,
        cellEditor: "agSelectCellEditor",
        refData: GROUP_FUNCTION, // dropdown-dataSource
        cellEditorParams: {
          allowTyping: true, filterList: true, highlightMatch: true,
          values: extractEnumKeys(GROUP_FUNCTION),
          // valueListMaxHeight: 220,
        },
        cellRendererParams: {
          onAddRowAbove: (rowIndex: number) => { },
          onAddRowBelow: (rowIndex: number) => { },
          onRemoveRow: (rowIndex: number) => { },
        },
        onCellValueChanged: (event: NewValueParams<IColumnAndFunction, any>) => {
          const currentFunctionKey = event.newValue as string;
          const currentRowIndex = event.node?.rowIndex;

          if (currentFunctionKey === NONE_KEY && event.node) {
            let isOtherNodesGrouped = false;
            event.api.forEachNode((otherNode, otherRowIndex) => {
              if (otherRowIndex !== currentRowIndex &&
                otherNode.data && otherNode.data['function'] !== NONE_KEY) {
                isOtherNodesGrouped = true;
              }
            });
            // if (isOtherNodesGrouped && event.node?.data) {
            //   event.node.data['function'] = event.oldValue;
            //   event.api.refreshCells();
            // }
          } else {
            event.api.forEachNode((otherNode, otherRowIndex) => {
              if (otherRowIndex !== currentRowIndex &&
                otherNode.data && otherNode.data['function'] === NONE_KEY
              ) {
                otherNode.data['function'] = GROUP_KEY;
              }
            });
            event.api.refreshCells();
          }


        },
      },
    ];
  }, [defaultAvailableColumns]);


  const getNewRow = (
    gridApi: TGridApi<IColumnAndFunction>
  ) => {
    const hasFunctions = isFunctionsApplied(gridApi);
    return {
      field: "-1",
      function: getKeyByValue(GROUP_FUNCTION,
        hasFunctions ? GROUP_FUNCTION.GROUP : GROUP_FUNCTION.NONE)!,
    };
  };

  const initializeRows = (
    gridApi: TGridApi<IColumnAndFunction>,
    selectedColumns: IReportSelectedColumn[]
  ) => {
    if (gridApi) {
      const newRows: IColumnAndFunction[] = [];

      // clear residues
      gridApi.updateGridOptions({ rowData: [] });

      selectedColumns.forEach(selColumn => {
        newRows.push({
          field: selColumn.columnName,
          function: selColumn.aggregateFunction ? selColumn.aggregateFunction :
            getKeyByValue(GROUP_FUNCTION, GROUP_FUNCTION.NONE)!,
        });
      });

      const length = gridApi.getDisplayedRowCount();
      gridApi.applyTransaction({
        addIndex: length,
        add: [...newRows],
      });
      gridApi.refreshCells();
    }

  };

  const getChanges = (gridApi: TGridApi<IColumnAndFunction>): IReportSelectedColumn[] => {
    const newColumns: IReportSelectedColumn[] = [];
    if (gridApi) {

      gridApi.forEachNode(node => {
        if (node.data) {
          const columnName = node.data['field'];
          const hasFunction = node.data['function'] !== NONE_KEY;
          let targetCol = defaultSelectedColumns.find(x => x.columnName === columnName)
          if (!targetCol) {
            targetCol = defaultAvailableColumns.find(x => x.columnName === columnName)
          }

          newColumns.push({
            ...targetCol,
            columnDataType: null,
            // ---
            aggregateFunction: hasFunction ? node.data['function'] : null,
            orderType: hasFunction ? 'ASC' : null,
            groupBy: hasFunction ? 'True' : null,
          } as IReportSelectedColumn)
        }
      });
    }
    // ---
    return newColumns;
  }

  const insertRow = (
    gridApi: TGridApi<IColumnAndFunction>,
    rowIndex?: number | null
  ) => {
    if (gridApi) {
      const length = gridApi.getDisplayedRowCount();
      gridApi.applyTransaction({
        addIndex: (rowIndex === undefined || rowIndex === null) ? length : rowIndex,
        add: [getNewRow(gridApi)],
      });
      gridApi.refreshCells();
    }
  }

  const removeRow = (
    gridApi: TGridApi<IColumnAndFunction>,
    nodeData?: IColumnAndFunction
  ) => {
    if (gridApi && nodeData) {
      gridApi.applyTransaction({
        remove: [nodeData]
      });
    }
  }

  const isFunctionsApplied = (gridApi: TGridApi<IColumnAndFunction>) => {
    let hasFunction: boolean = false;

    if (gridApi) {
      gridApi.forEachNode(node => {
        if (node.data && node.data['function'] !== NONE_KEY) {
          hasFunction = true;
        }
      });
    }

    return hasFunction;
  }

  const clearGroupings = (gridApi: TGridApi<IColumnAndFunction>) => {
    if (gridApi) {
      gridApi.forEachNode((node, index) => {
        if (node.data) {
          node.data['function'] = NONE_KEY;
        }
      });
      gridApi.refreshCells();
    }
  }

  // ---
  return {
    initializeRows, insertRow, removeRow, getNewRow, isFunctionsApplied,
    clearGroupings, getColDefs, getSaveSelectedColumnsBody,
    getChanges,
  };
}